import { NetworkIntegrityAggregateGroup,  NetworkIntegrityAggregateKey, ProjectRunConfigurationType } from "@app/projects/enumerators";
import { NetworkIntegrityProvider } from "@app/projects/network-assessment/network-integrity/models/networkIntegrityProvider";
import { FlyoutContext, FlyoutFooter } from "@app/shared/Flyouts";
import { JSX, useContext, useEffect, useState } from "react";
import { useInfiniteScroll } from "@app/shared/useInfiniteScroll";
import ProviderTerminationService from "@app/provider-terminations/providerTerminationService";
import { SpecialtyGroupCodeToProviderIdsModel } from "@app/projects/specialtyGroupCodeToProviderIdsModel";
import { forEach } from "lodash";
import { toast } from "react-toastify";
import { Accordion } from "@app/shared/Accordion";
import InfoIndicator from "@app/shared/InfoIndicator";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESRadioButton } from "@app/shared/qes-standards/forms/RadioButton";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextArea } from "@app/shared/qes-standards/forms/TextArea";

interface NetworkIntegrityTerminationFlyoutProps {
  aggregateGroup: NetworkIntegrityAggregateGroup;
  aggregateType: string;
  projectId: number;
  fetchSelectedProviders: () => Promise<NetworkIntegrityProvider[]>;
  selectionCount: number;
  projectRunConfigurationDualType: ProjectRunConfigurationType;
}

const NetworkIntegrityTerminationFlyout = (props: NetworkIntegrityTerminationFlyoutProps): JSX.Element => {
  const flyoutContext = useContext(FlyoutContext);
  const [isAllLocations, setIsAllLocations] = useState<boolean>(false);
  const [isAllSpecialties, setIsAllSpecialties] = useState<boolean>(false);
  const [comment, setComment] = useState<string>("");
  const npiRecordingCriteria = "NPI";
  const nameRecordingCriteria = "Name";
  const [recordingCriteria, setRecordingCriteria] = useState<string>(props.aggregateType == NetworkIntegrityAggregateKey.MissingNpi ? nameRecordingCriteria : npiRecordingCriteria);
  const [error, setError] = useState<JSX.Element>(null);
  const [selectedProviders, setSelectedProviders] = useState<NetworkIntegrityProvider[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const PAGE_SIZE = 100;
  const [visibleCount, setVisibleCount] = useState<number>(PAGE_SIZE);
  const hasMore = visibleCount < selectedProviders.length;
  const visibleProviders = selectedProviders.slice(0, visibleCount);
  const loadMore = () => setVisibleCount(prev => prev + PAGE_SIZE);
  const { sentinelRef } = useInfiniteScroll(loadMore, hasMore);

  const providerTerminationService = new ProviderTerminationService();

  const flyoutTitle = (): string => {
    switch (props.aggregateGroup){
      case NetworkIntegrityAggregateGroup.ExcessSpecialties:
        return `Providers With ${props.aggregateType} Specialties`;
      case NetworkIntegrityAggregateGroup.ExcessAddresses:
        return `Providers With ${props.aggregateType} Addresses`;
    };

    switch (props.aggregateType) {
      case NetworkIntegrityAggregateKey.DeactivatedNpi:
        return "Deactivated NPI";
      case NetworkIntegrityAggregateKey.InvalidNpi:
        return "Invalid NPI";
      case NetworkIntegrityAggregateKey.MedicarePrecluded:
        return "Medicare Preclusion";
      case NetworkIntegrityAggregateKey.MissingNpi:
        return "Missing NPI";
      default:
        return props.aggregateType;
    };
  };

  const terminatingReason = (): string => {
    const niPrefix = "Network Integrity:";
    let reason: string;
    switch (props.aggregateGroup) {
      case NetworkIntegrityAggregateGroup.ExcessSpecialties:
        reason = 'Excess Specialties';
        break;
      case NetworkIntegrityAggregateGroup.ExcessAddresses:
        reason = 'Excess Addresses';
        break;
      default:
        reason = flyoutTitle();
        break;
    }

    reason = `${niPrefix} ${reason}`;
    return reason.length <= 255 ? reason : reason.substring(0, 255);
  };

  const getTerminationCheckboxTooltip = (checkboxType: "AllLocations" | "AllSpecialties", checked: boolean): string => {
    switch (checkboxType) {
      case "AllLocations":
        if (checked)
          return `Selected Termination reasoning (${terminatingReason()}) applies to all locations for this provider.`;
        else
          return `Selected Termination reason (${terminatingReason()}) applies only to the specific locations identified.`;
      case "AllSpecialties":
        if (checked)
          return `Selected Termination reasoning (${terminatingReason()}) applies to all specialties for this provider.`;
        else
          return `Selected Termination reason (${terminatingReason()}) applies only to the specific specialties identified.`;
    }
  };

  useEffect(() => {
    setLoading(true);
    const fetchProviders = async () => {
      try {
        const providers = await props.fetchSelectedProviders();
        setSelectedProviders(providers);
        setVisibleCount(PAGE_SIZE);
      } finally {
        setLoading(false);
      }
    };
    fetchProviders();
  }, []);

  useEffect(() => {
    setDefaultValues();
  }, [props.aggregateGroup, props.aggregateType]);

  const setDefaultValues = () => {
    setIsAllLocations(false);
    setIsAllSpecialties(false);
    setComment("");

    //Ghost provider by LOB also needs to be all specialties and all locations, but the LOB is a dynamic aggregateType
    const aggregateType = props.aggregateType.startsWith(NetworkIntegrityAggregateKey.GhostProviders)
      ? NetworkIntegrityAggregateKey.GhostProviders
      : props.aggregateType;

    if(aggregateType == NetworkIntegrityAggregateKey.MissingNpi)
      setRecordingCriteria(nameRecordingCriteria);
    else
      setRecordingCriteria(npiRecordingCriteria);

    switch (aggregateType) {
      case NetworkIntegrityAggregateKey.GhostProviders:
      case NetworkIntegrityAggregateKey.ExpiredLicensePractitioners:
      case NetworkIntegrityAggregateKey.RetiredPractitioner:
      case NetworkIntegrityAggregateKey.DeactivatedNpi:
      case NetworkIntegrityAggregateKey.InvalidNpi:
      case NetworkIntegrityAggregateKey.OigExclusion:
      case NetworkIntegrityAggregateKey.MedicareOptOut:
      case NetworkIntegrityAggregateKey.MedicarePrecluded:
        setIsAllLocations(true);
        setIsAllSpecialties(true);
        break;
      case NetworkIntegrityAggregateKey.LowSpecialtyEvidence:
        setIsAllLocations(true);
        setIsAllSpecialties(false);
        break;
      case NetworkIntegrityAggregateKey.LowLocationConfidence:
      case NetworkIntegrityAggregateKey.VeryLowLocationConfidence:
      case NetworkIntegrityAggregateKey.ClosedLocation:
      case NetworkIntegrityAggregateKey.LeftOffice:
      case NetworkIntegrityAggregateKey.NotRecognized:
      case NetworkIntegrityAggregateKey.NoLongerOnRoster:
      case NetworkIntegrityAggregateKey.TermedOnRoster:
      case NetworkIntegrityAggregateKey.GeoCodeZipDistributive:
        setIsAllLocations(false);
        setIsAllSpecialties(true);
        break;
      case NetworkIntegrityAggregateKey.MissingNpi:
        setIsAllLocations(false);
        setIsAllSpecialties(false);
        break;
    }
  };

  function handleTerminationSubmit(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
    event.preventDefault();
    const providersBySpecialtyCode: SpecialtyGroupCodeToProviderIdsModel[] = [];

    forEach(selectedProviders, (provider) => {
      const existingEntry = providersBySpecialtyCode.find(entry => entry.specialtyGroupCode === provider.specialty);
      if (existingEntry) {
        existingEntry.providerIds.push(provider.providerId);
      } else {
        providersBySpecialtyCode.push({
          providerIds: [provider.providerId],
          specialtyGroupCode: provider.specialty
        });
      }
    });

    providerTerminationService.createNetworkIntegrityProviderTerminations(
      props.projectId,
      comment,
      isAllSpecialties,
      isAllLocations,
      providersBySpecialtyCode,
      props.projectRunConfigurationDualType,
      recordingCriteria == npiRecordingCriteria,
      terminatingReason()
    ).then((): void => {
            toast.info(<span>Provider Termination(s) Now Pending</span>,
              {
                closeOnClick: true,
                containerId: "z-index-9999",
                draggable: false,
              });
            flyoutContext.handleCloseFlyout();
          },
          (reject) => setError(<QESAlert type="danger">{reject.responseText || "Unable to complete termination because one or more selected ghost providers could not be matched. Please try again or contact support if the issue continues."}</QESAlert>));
  }


  return (
    <div className="ni-termination-flyout">
      {error}
      <h4>{flyoutTitle()}</h4>
      <div className="ps-3 pe-3 mb-1 d-flex">
        <QESCheckbox
          containerClassName="ni-termination-flyout__checkbox-tooltip"
          disabled={true}
          label="All Locations"
          checked={isAllLocations}
          testId="ni-terminate-providers-flyout__is-all-locations"
        />
        &nbsp;
       <InfoIndicator tooltipTitle={getTerminationCheckboxTooltip("AllLocations", isAllLocations)} tooltipPlacement="right" />
       </div>
      <div className="ps-3 pe-3 mb-3 d-flex">
        <QESCheckbox
          containerClassName="ni-termination-flyout__checkbox-tooltip"
          disabled={true}
          label="All Specialties"
          checked={isAllSpecialties}
          testId="ni-terminate-providers-flyout__is-all-specialties"
        />
        &nbsp;
        <InfoIndicator tooltipTitle={getTerminationCheckboxTooltip("AllSpecialties", isAllSpecialties)} tooltipPlacement="right" />
      </div>
      <div className="row">
        <div className="col-12">
          <Accordion
            title={`Selected Providers (${props.selectionCount.toLocaleString()})`}
            testId="selected-providers-accordion"
            defaultOpen={props.selectionCount <= 25}
          >
            {loading && <LoadingSpinner/>}
            {!loading &&
              <div style={{ maxHeight: "350px", overflowY: "auto" }}>
                <ul>
                  {visibleProviders.map((provider) => (
                    <li key={`${provider.providerId}|${provider.providerSetVersionId}`}>
                      {provider.providerName} | {provider.npi} | {provider.address1} {provider.address2} {provider.city}, {provider.state} {provider.zip} | {provider.specialty} | {provider.specialtyName}
                    </li>
                  ))}
                </ul>
                <div ref={sentinelRef} style={{ height: "1px" }} />
              </div>
            }
          </Accordion>
        </div>
      </div>
      <div className="ps-3 pt-3 pe-3 mb-3">
        <p className="qes-text-input__label">Recording Criteria:</p>
        <QESRadioButton
          label={recordingCriteria}
          value={recordingCriteria}
          defaultChecked={true}
          testId="ni-terminate-providers-flyout__recording-criteria-npi"
        />
      </div>
      <div className="ps-3 pt-3 pe-3 mb-3">
        The Reason and Comment entered below will be visible to any other users within your organization with the Termination Viewer or Termination Provider Admin role.
      </div>
      <div className="row ps-3 pe-3 mb-3">
        <QESSelect
          label="Reason"
          containerClassName="col-3"
          required={true}
          disabled={true}
          testId="ni-terminate-providers-flyout__reason-select">
          <option value="">
            Network Integrity
          </option>
        </QESSelect>
      </div>
      <div className="row ps-3 pe-3 mb-3">
        <QESTextArea
          label="Comment"
          containerClassName="col-6"
          value={comment}
          maxLength={1000}
          onChange={(e) => setComment(e.target.value)}
          testId="ni-terminate-providers-flyout__comment"
        />
      </div>
      <FlyoutFooter
        submitButton={
          <QESButton
            className="cursor-pointer ms-3"
            disabled={terminatingReason().length === 0}
            onClick={handleTerminationSubmit}
            qesTestId="ni-terminate-providers-flyout__submit-button"
            qesType="primary"
          >
            Submit
          </QESButton>
        }
        cancelButtonText="Cancel"
      />
    </div>
  );
};

export { NetworkIntegrityTerminationFlyout };